import React, { useEffect, useMemo, useState } from 'react';
import { Alert, App, Button, Grid, Image, Progress, Space, Steps, Tag, Timeline, Tooltip, Typography } from 'antd';
import {
  CalendarFilled, CheckCircleFilled, ClockCircleOutlined, CopyFilled, DollarCircleFilled, EnvironmentFilled, ExclamationCircleFilled,
  PaperClipOutlined, QuestionCircleFilled, SwapOutlined, WarningFilled,
} from '@ant-design/icons';
import axios from 'axios';
import dayjs from 'dayjs';
import { API_BASE_URL } from '../../utils/config';
import { useAuth } from '../../context/AuthContext';

/** Harcırah ekranlarının ortak parçaları (routes/harcirah.py: /harcirah yönetici, /harcirah-benim çalışan). */

export const GUNLER = ['Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt', 'Paz'];
export const AYLAR = ['Oca', 'Şub', 'Mar', 'Nis', 'May', 'Haz', 'Tem', 'Ağu', 'Eyl', 'Eki', 'Kas', 'Ara'];
export const OGUN_ADI = { ogle: 'Öğle', aksam: 'Akşam' };
// Harcırah tipi (services/harcirah.py TIPLER): hangi öğünlerin hakkı açılır
export const TIP_ADI = { A: 'Öğle + Akşam', B: 'Yalnız Akşam', C: 'Yalnız Öğle' };
export const TIP_RENK = { A: 'blue', B: 'purple', C: 'cyan' };
// Seyahat giderleri (services/harcirah.py GIDER_TURLERI / ULASIM); simgeli tanımlar giderler.jsx'te
export const GIDER_ADI = { yakit: 'Yakıt', ucak: 'Uçak Bileti', arac_kiralama: 'Araç Kiralama', konaklama: 'Konaklama' };
export const ULASIM_ADI = { sirket_araci: 'Şirket Aracı', ucak: 'Uçak + Kiralık Araç' };
// Seyahat / rezervasyon akışı (26.10.2026; services/harcirah.py REZ_ASAMALAR) — kısa adlar
export const REZ_ASAMA_ADI = {
  plan: 'Plan', amir: 'Amir Onayı', fiyat: 'Rezervasyon Fiyatı', finans_onay: 'Finans Fiyat Onayı',
  islem: 'Ödeme Ve İşlem', finans_kontrol: 'Finans Harcama Kontrolü', yonetici: 'Yönetici Onayı', tamam: 'Tamamlandı',
};
const REZ_SIRA = ['plan', 'amir', 'fiyat', 'finans_onay', 'islem', 'finans_kontrol', 'yonetici', 'tamam'];
const REZ_ADIM_KISA = {
  plan: 'Plan', amir: 'Amir', fiyat: 'Fiyat', finans_onay: 'Finans Onayı', islem: 'Ödeme / İşlem',
  finans_kontrol: 'Finans Kontrol', yonetici: 'Yönetici', tamam: 'Tamam',
};
const REZ_KIM = {
  plan: 'Personel', amir: 'Amir', fiyat: 'Rezervasyon', finans_onay: 'Finans', islem: 'Rezervasyon',
  finans_kontrol: 'Finans', yonetici: 'Yönetici',
};

/** Seyahat akışının adımları: otel / uçak gerekmiyorsa plan → amir → tamam. Eski kayıtta (akış öncesi) çizilmez. */
export function SeyahatAkisi({ s, kompakt = false }) {
  const genis = Grid.useBreakpoint().md;
  if (!s.rez_durum) return null;
  const adimlar = s.rez_gerekli ? REZ_SIRA : ['plan', 'amir', 'tamam'];
  const i = Math.max(0, adimlar.indexOf(s.rez_durum));
  const geri = [...(s.gecmis || [])].reverse().find((g) => ['rez_geri', 'rez_onay', 'rez_ilerlet', 'plan_gonder'].includes(g.islem));
  const geriDondu = geri?.islem === 'rez_geri' && geri.hedef === s.rez_durum;
  if (!genis) {  // dar ekranda adım çizgisi sığmıyor: "Adım 3/8" + sıradaki
    const sonraki = adimlar.at(i + 1);
    return (
      <div style={{ fontSize: 13 }}>
        <div>Adım {i + 1}/{adimlar.length}: <b>{REZ_ASAMA_ADI[s.rez_durum]}</b>{REZ_KIM[s.rez_durum] ? ` (${REZ_KIM[s.rez_durum]})` : ''}
          {sonraki ? <Typography.Text type="secondary"> · Sonra: {REZ_ASAMA_ADI[sonraki]}</Typography.Text> : null}</div>
        <Progress percent={Math.round(((i + (s.rez_durum === 'tamam' ? 1 : 0)) / (adimlar.length - 1)) * 100)} size="small"
          showInfo={false} status={geriDondu ? 'exception' : 'normal'} style={{ margin: 0 }} />
      </div>
    );
  }
  return (
    <Steps size="small" labelPlacement="vertical" current={i} status={s.rez_durum === 'tamam' ? 'finish' : geriDondu ? 'error' : 'process'}
      responsive={false}
      items={adimlar.map((a) => ({
        title: <span style={{ fontSize: 12, whiteSpace: 'nowrap' }}>{REZ_ADIM_KISA[a]}</span>,
        description: !kompakt && REZ_KIM[a] ? <span style={{ fontSize: 11 }}>{REZ_KIM[a]}</span> : null,
      }))} />
  );
}
export const DURUM_RENK = {
  taslak: 'default', amir: 'processing', finans: 'processing', son: 'processing', onaylandi: 'success', geri: 'error',
};

export const para = (v) => `${Number(v || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ₺`;
export const kisaPara = (v) => Number(v || 0).toLocaleString('tr-TR', { maximumFractionDigits: 2 });
export const tarih = (v) => (v ? dayjs(v).format('DD.MM.YYYY') : '');
export const kisaTarih = (v) => (v ? dayjs(v).format('DD.MM') : '');
export const gunAdi = (v) => GUNLER[(dayjs(v).day() + 6) % 7];
export const pazartesi = (d) => dayjs(d).startOf('day').subtract((dayjs(d).day() + 6) % 7, 'day');
export const ogunTutari = (g, o) => (g[o] ? g[`${o}_tutar`] || 0 : 0);
export const gunTutari = (g) => ogunTutari(g, 'ogle') + ogunTutari(g, 'aksam');
// Başlık biçimi (30.10.2026 kullanıcı): düğme, etiket ve sabit kısa yazılarda her kelimenin baş harfi büyük; cümleler
// (uyarı, açıklama, sıradaki adım) hariç. CSS text-transform: capitalize Chrome'da Türkçe i → İ yapmıyor ("Işlem"),
// bu yüzden tr yerel ayarlı JS.
export const bb = (m) => (typeof m === 'string'
  ? m.replace(/(^|[\s\-/(+·–])(\p{L})/gu, (_, a, h) => a + h.toLocaleUpperCase('tr')) : m);
// Sunucudan gelen görünen adlar (durum, aşama, tür, ulaşım; seçenek listeleri; rapordaki tür / gider / ulaşım adları)
// ekranda başlık biçiminde; backend adları (geçmişe yazılanlar dahil) değişmez.
const BASLIK_ALANLARI = new Set(['durum_adi', 'rez_durum_adi', 'son_durum_adi', 'asama_adi', 'seyahat_turu_adi', 'ulasim_adi']);
const basliklastir = (v) => {
  if (Array.isArray(v)) return v.map(basliklastir);
  if (!v || typeof v !== 'object') return v;
  return Object.fromEntries(Object.entries(v).map(([k, x]) => [k,
    BASLIK_ALANLARI.has(k) || (k === 'label' && 'value' in v) || (k === 'ad' && ('tur' in v || 'ulasim' in v || 'ekip' in v)) ? bb(x) : basliklastir(x)]));
};

export const hata = (e) => {
  const d = e?.response?.data?.detail;
  return typeof d === 'string' ? d : e?.message || 'İşlem başarısız.';
};

// getAuthHeaders her render'da yeni; istemci yalnız token değişince yenilenir (ortak/moduleApi gibi)
export function useHarcirahApi(kok = '/harcirah') {
  const { getAuthHeaders, token } = useAuth();
  return useMemo(() => {
    const cfg = () => ({ headers: getAuthHeaders() });
    const root = `${API_BASE_URL}${kok}`;
    const veri = (r) => basliklastir(r.data.data);
    return {
      get: (yol, params = {}) => axios.get(`${root}${yol}`, { ...cfg(), params }).then(veri),
      post: (yol, govde) => axios.post(`${root}${yol}`, govde, cfg()).then(veri),
      put: (yol, govde) => axios.put(`${root}${yol}`, govde, cfg()).then(veri),
      del: (yol, params) => axios.delete(`${root}${yol}`, { ...cfg(), params }).then(veri),
      // multipart: Content-Type'ı tarayıcı (boundary ile) koyar
      form: (yol, formData) => axios.post(`${root}${yol}`, formData, {
        headers: { Authorization: getAuthHeaders().Authorization },
      }).then(veri),
      blob: (yol, params = {}) => axios.get(`${root}${yol}`, { ...cfg(), params, responseType: 'blob' }).then((r) => r.data),
    };
  }, [token, kok]); // eslint-disable-line react-hooks/exhaustive-deps
}

// Öğün fişi (tarih + öğün) ya da gider belgesi (giderId)
const belgeIndir = (api, seyahatId, gunTarihi, ogun, giderId) => (giderId
  ? api.blob(`/gider-fis/${seyahatId}/${giderId}`)
  : api.blob(`/fis/${seyahatId}`, { tarih: gunTarihi, ogun }));

/** Fiş düğmesi: dosya oturumla indirilir (özel bucket); resim önizlenir, PDF/HEIC yeni sekmede açılır. */
export function FisDugmesi({ seyahatId, gunTarihi, ogun, giderId, fis, etiket }) {
  const api = useHarcirahApi('/harcirah-benim');
  const { message } = App.useApp();
  const [url, setUrl] = useState(null);
  useEffect(() => () => { if (url) URL.revokeObjectURL(url); }, [url]);
  if (!fis) return null;

  const ac = () => belgeIndir(api, seyahatId, gunTarihi, ogun, giderId).then((b) => {
    const u = URL.createObjectURL(b);
    if (['image/jpeg', 'image/png', 'image/webp'].includes(fis.tur)) {
      setUrl(u);
      return;
    }
    const a = document.createElement('a');
    a.href = u;
    a.target = '_blank';
    if (fis.tur === 'image/heic') a.download = giderId ? `gider_${seyahatId}_${giderId}.heic` : `fis_${seyahatId}_${gunTarihi}_${ogun}.heic`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(u), 60000);
  }).catch((e) => message.error(hata(e)));

  return (
    <>
      <Tooltip title={giderId ? 'Gider Belgesi' : `${OGUN_ADI[ogun]} Fişi`}>
        <Button size="small" type="link" icon={<PaperClipOutlined />} onClick={ac} style={{ padding: '0 4px' }}>
          {etiket}
        </Button>
      </Tooltip>
      {url && (
        <Image style={{ display: 'none' }} src={url}
          preview={{ visible: true, src: url, onVisibleChange: (v) => { if (!v) setUrl(null); } }} />
      )}
    </>
  );
}

// --- Fiş kontrol işaretleri (services/harcirah.py ogun_isaretleri) ---------------------------
const kisaGun = (t) => (t ? `${t.slice(8, 10)}.${t.slice(5, 7)}` : '');
const yerAdi = (y) => (y.ogun === 'gider' ? y.gider_adi : OGUN_ADI[y.ogun]);
// Tarih farkı: öğün fişi (fiş ↔ öğün günü) ya da konaklama belgesi (belgedeki dönem ↔ girilen dönem; tek tarihli
// belge ↔ seyahat tarihleri)
const tarihAyrinti = (i) => {
  if (i.okunan_giris) {
    return `Belgede: ${tarih(i.okunan_giris)} – ${tarih(i.okunan_cikis)} · ${i.girilen_giris
      ? `Girilen: ${tarih(i.girilen_giris)} – ${tarih(i.girilen_cikis)}` : `Seyahat: ${tarih(i.seyahat_bas)} – ${tarih(i.seyahat_bit)}`}`;
  }
  if (i.seyahat_bas) return `Belge: ${tarih(i.okunan)} · Seyahat: ${tarih(i.seyahat_bas)} – ${tarih(i.seyahat_bit)}`;
  return `Fiş: ${tarih(i.okunan)} · Öğün: ${tarih(i.gun)}${gunFarki(i.okunan, i.gun)}`;
};
export const isaretMetni = (i) => ({
  tutar: `Fişten ${para(i.okunan)} okundu, ${para(i.girilen)} girildi`,
  tarih: tarihAyrinti(i),
  sehir: `Fiş ${i.okunan} adresli, seyahat ${i.seyahat}`,
  ayni: `Aynı fiş başka yerde de var: ${(i.yerler || []).map((y) => `${y.kisi} ${kisaGun(y.tarih)} ${yerAdi(y)}`).join(', ')}`,
  okunamadi: 'Fiş otomatik okunamadı; tutar elle girildi',
}[i.tur] || i.tur);

// Ciddi (kırmızı): şehir, tarih, aynı fiş · uyarı (turuncu): tutar farkı · bilgi (gri): okunamadı.
// Renk tek başına anlam taşımaz: her etikette simge + BÜYÜK HARF ad + ayrıntı var.
// Tarih farkı: tam tarih + gün farkı (aylar önceki fiş "23.04" diye kaybolmasın)
const gunFarki = (fis, gun) => {
  const f = dayjs(gun).diff(dayjs(fis), 'day');
  if (!f) return '';
  return f > 0 ? ` · ${f} gün önce` : ` · ${-f} gün sonra`;
};
export const ISARET_TANIM = {
  sehir: { ad: 'ŞEHİR FARKLI', kisaAd: 'ŞEHİR', renk: 'error', ikon: <EnvironmentFilled />, ciddi: true,
    ayrinti: (i) => `Fiş: ${i.okunan}${i.adres_turu === 'merkez' ? ' (merkez adresi)' : ''} · Seyahat: ${i.seyahat}` },
  tarih: { ad: 'TARİH FARKLI', kisaAd: 'TARİH', renk: 'error', ikon: <CalendarFilled />, ciddi: true,
    ayrinti: tarihAyrinti },
  ayni: { ad: 'AYNI FİŞ', kisaAd: 'AYNI', renk: 'error', ikon: <CopyFilled />, ciddi: true,
    ayrinti: (i) => (i.yerler || []).map((y) => `${y.kisi} ${kisaGun(y.tarih)} ${yerAdi(y)}`).join(', ') },
  tutar: { ad: 'TUTAR FARKLI', kisaAd: 'TUTAR', renk: 'warning', ikon: <SwapOutlined />, ciddi: false,
    ayrinti: (i) => `Okunan ${para(i.okunan)} · Girilen ${para(i.girilen)}` },
  okunamadi: { ad: 'OKUNAMADI', kisaAd: 'OKUNAMADI', renk: 'default', ikon: <QuestionCircleFilled />, ciddi: false,
    ayrinti: () => 'Fiş otomatik okunamadı' },
  limit: { ad: 'LİMİT AŞILDI', kisaAd: 'LİMİT', renk: 'error', ikon: <DollarCircleFilled />, ciddi: true,
    ayrinti: () => 'Rezervasyon tutarı finansın onayladığı limiti aşıyor' },
};
// Açıklama için hazır nedenler (çalışan tıklar, gerekirse düzenler). Şehir farkında ilk sıradaki: fişlerde sık görülen
// "işletmenin merkez adresi" durumu (kullanıcı isteği 24.10.2026).
export const NEDEN_ONERILERI = {
  sehir: ['Fişte işletmenin merkez adresi yazıyor; yemek seyahat ilindeki şubede yendi',
    'Yolculuk sırasında başka bir ilde yendi', 'Seyahat güzergâhı / planı değişti'],
  tarih: ['Gece yarısından sonra yendi', 'Fiş tarihi yazarkasada yanlış basılmış', 'Fiş sonradan alındı'],
  tutar: ['Fiş yanlış okundu, doğru tutarı girdim', 'Servis ücreti / bahşiş eklendi', 'Hesabın yalnız bana düşen payını girdim'],
};
export const ciddiMi = (isaretler) => (isaretler || []).some((i) => ISARET_TANIM[i.tur]?.ciddi);

// Günlük konum doğrulaması (03.11.2026, routes/harcirah.py /konum): gün başına durum. Ekrana koordinat gelmez; yalnız sonuç,
// saat ve sunucunun mesajı (hangi ilde göründüğü). Gelecek ve özellik öncesi (muaf) günlerde etiket yok.
export const KONUM_TANIM = {
  dogrulandi: { ad: 'Konum Doğrulandı', renk: 'success', ikon: <CheckCircleFilled /> },
  bugun: { ad: 'Konum Bekleniyor', renk: 'warning', ikon: <ClockCircleOutlined /> },
  eksik: { ad: 'Konum Doğrulanmadı', renk: 'error', ikon: <ExclamationCircleFilled /> },
};
export const konumIpucu = (o) => (o.durum === 'dogrulandi' ? `${o.saat} · ${o.mesaj}`
  : o.mesaj ? `Son deneme ${o.saat}: ${o.mesaj}` : 'Bu gün konum doğrulaması yapılmadı.');
// Doğrulanmamış ve açıklaması da olmayan günler (bugün dahil): gönderim bunlar bitmeden yapılamaz
export const konumBekleyen = (s) => Object.entries(s.konum?.gunler || {})
  .filter(([, o]) => (o.durum === 'eksik' || o.durum === 'bugun') && !o.aciklama).map(([t]) => t);

export function KonumEtiketi({ o }) {
  const t = o && KONUM_TANIM[o.durum];
  if (!t) return null;
  return (
    <Tooltip title={konumIpucu(o)}>
      <Tag color={t.renk} icon={t.ikon} style={{ marginInlineEnd: 0, cursor: 'help' }}>
        {o.durum === 'dogrulandi' ? `Konum ${o.saat}` : t.ad}
      </Tag>
    </Tooltip>
  );
}

/** Öğün işaretleri: etiket + ayrıntı + (şehir/tarih için) çalışanın açıklaması. kisa: yalnız ad. */
export function IsaretEtiketleri({ isaretler, kisa = false, aciklamaAdi = 'Çalışanın Açıklaması' }) {
  if (!isaretler?.length) return null;
  const aciklama = isaretler.find((i) => i.aciklama)?.aciklama;
  return (
    <div style={{ marginTop: 4, textAlign: 'left' }}>
      <Space size={[4, 4]} wrap>
        {isaretler.map((i) => {
          const t = ISARET_TANIM[i.tur] || { ad: i.tur, renk: 'default' };
          return (
            <Tooltip key={i.tur} title={kisa ? `${t.ad}: ${t.ayrinti?.(i) || ''}` : null}>
              <Tag color={t.renk} icon={t.ikon} style={{ marginInlineEnd: 0, fontWeight: t.ciddi ? 600 : 400,
                ...(kisa ? {} : { whiteSpace: 'normal', height: 'auto', lineHeight: '18px', paddingBlock: 1 }) }}>
                {kisa ? t.kisaAd || t.ad : t.ad}{!kisa && t.ayrinti ? <span style={{ fontWeight: 400 }}> · {t.ayrinti(i)}</span> : null}
              </Tag>
            </Tooltip>
          );
        })}
      </Space>
      {!kisa && aciklama && (
        <Typography.Text style={{ display: 'block', fontSize: 12, marginTop: 2 }}>
          <b>{aciklamaAdi}:</b> <i>{aciklama}</i>
        </Typography.Text>
      )}
    </div>
  );
}

const isaretSay = (kaynak) => {
  const sayi = {};
  Object.values(kaynak || {}).forEach((l) => l.forEach((i) => { sayi[i.tur] = (sayi[i.tur] || 0) + 1; }));
  return Object.keys(ISARET_TANIM).filter((t) => sayi[t]).map((t) => [t, sayi[t]]);
};

/** Seyahat başı bandı: işaret türlerine göre öğün ve gider sayıları. isaretler = seyahat.isaretler ({"tarih|ogun": [...]}),
 * giderIsaretleri = seyahat.gider_isaretleri ({gider id: [...]}) */
export function IsaretBandi({ isaretler, giderIsaretleri, kime = 'onaycı' }) {
  const ogun = isaretSay(isaretler);
  const gider = isaretSay(giderIsaretleri);
  if (!ogun.length && !gider.length) return null;
  const ciddi = [...ogun, ...gider].some(([t]) => ISARET_TANIM[t].ciddi);
  const etiket = (yer) => ([t, n]) => (
    <Tag key={`${yer}-${t}`} color={ISARET_TANIM[t].renk} icon={ISARET_TANIM[t].ikon} style={{ fontSize: 13, padding: '2px 8px' }}>
      {n} {yer} {ISARET_TANIM[t].ad}
    </Tag>
  );
  return (
    <Alert type={ciddi ? 'error' : 'warning'} showIcon icon={<WarningFilled />} style={{ marginBottom: 12 }}
      message={<b>{ciddi ? 'DİKKAT — bu seyahatte kontrol gerektiren belgeler var' : 'Bu seyahatte kontrol işaretleri var'}</b>}
      description={(
        <Space size={[6, 6]} wrap>
          {ogun.map(etiket('Öğünde'))}
          {gider.map(etiket('Giderde'))}
          {kime === 'calisan' && <span style={{ fontSize: 12 }}>Onaycılar bu işaretleri ve açıklamalarınızı görecek.</span>}
        </Space>
      )} />
  );
}

/** İşaret simgesi: yoksa hiçbir şey çizmez. Renk tek başına anlam taşımaz (simge + ipucu metni). */
export function IsaretSimgesi({ isaretler }) {
  if (!isaretler?.length) return null;
  const yalnizOkunamadi = isaretler.every((i) => i.tur === 'okunamadi');
  return (
    <Tooltip title={<div>{isaretler.map((i) => <div key={i.tur}>• {isaretMetni(i)}</div>)}</div>}>
      <WarningFilled aria-label="Kontrol işareti" style={{ color: yalnizOkunamadi ? '#8c8c8c' : '#fab219', marginLeft: 4, cursor: 'help' }} />
    </Tooltip>
  );
}

/** Fotoğrafı yüklemeden önce küçültür (uzun kenar 2000 px, JPEG); PDF/HEIC ve hata durumunda dosya aynen döner. */
export async function fisiKucult(dosya) {
  if (!['image/jpeg', 'image/png', 'image/webp'].includes(dosya.type)) return dosya;
  try {
    const bmp = await createImageBitmap(dosya);
    const oran = Math.min(1, 2000 / Math.max(bmp.width, bmp.height));
    if (oran === 1 && dosya.size < 1.5 * 1024 * 1024) return dosya;
    const tuval = document.createElement('canvas');
    tuval.width = Math.round(bmp.width * oran);
    tuval.height = Math.round(bmp.height * oran);
    tuval.getContext('2d').drawImage(bmp, 0, 0, tuval.width, tuval.height);
    const blob = await new Promise((ok) => tuval.toBlob(ok, 'image/jpeg', 0.85));
    return blob ? new File([blob], dosya.name.replace(/\.[^.]+$/, '') + '.jpg', { type: 'image/jpeg' }) : dosya;
  } catch {
    return dosya;
  }
}

// --- Geçmiş (seyahatin `gecmis` JSON'u) ---------------------------------------------------------
const ISLEM_ADI = {
  olustur: 'Oluşturuldu', duzenle: 'Düzenlendi', ogun: 'Öğün Hakkı Değişti', sil: 'Silindi',
  tutar_gir: 'Tutar Girildi', gonder: 'Onaya Gönderildi', geri_cek: 'Gönderim Geri Çekildi',
  onay: 'Onaylandı', geri_gonder: 'Geri Gönderildi', test_verisi: 'Test Verisi',
  ulasim: 'Ulaşım Şekli Seçildi', gider_gir: 'Gider Girildi', gider_sil: 'Gider Silindi',
  plan_gonder: 'Plan Amire Gönderildi', plan_geri_cek: 'Plan Geri Çekildi', rez_ilerlet: 'Seyahat Akışı İlerledi',
  rez_onay: 'Seyahat Onayı', rez_geri: 'Seyahat Geri Gönderildi',
};
const ALAN_ADI = {
  tip: 'Tip', amir: 'Amir', il: 'İl', aciklama: 'Açıklama', tarih: 'Tarih', seyahat_turu: 'Seyahat Türü', ulasim: 'Ulaşım',
  konaklama: 'Otel', rez_notu: 'Rezervasyon Talebi',
};
export const ASAMA_ADI = { amir: 'Amir Onayı', finans: 'Finans Onayı', son: 'Son Onay' };

export function GecmisListesi({ gecmis }) {
  const satirlar = [...(gecmis || [])].reverse();
  return (
    <Timeline
      items={satirlar.map((g) => {
        let detay = null;
        if (g.islem === 'ogun') {
          detay = `${tarih(g.tarih)} ${OGUN_ADI[g.ogun]} hakkı ${g.yeni ? 'açıldı' : 'kapatıldı'}`;
        } else if (g.islem === 'tutar_gir') {
          detay = `${tarih(g.tarih)} ${OGUN_ADI[g.ogun]}: ${g.eski == null ? '—' : para(g.eski)} → ${g.yeni == null ? 'silindi' : para(g.yeni)}${g.fis ? ' · yeni fiş' : ''}${g.aciklama ? ` · açıklama: “${g.aciklama}”` : ''}`;
        } else if (g.islem === 'gonder') {
          detay = `Toplam ${para(g.toplam)} · ${g.girilen}/${g.acik} öğün${g.gider_toplam != null ? ` · şirket giderleri ${para(g.gider_toplam)}` : ''}`;
        } else if (g.islem === 'ulasim') {
          detay = `${g.eski || '—'} → ${g.yeni}`;
        } else if (g.islem === 'gider_gir') {
          detay = `${GIDER_ADI[g.gider_turu] || g.gider_turu}: ${g.eski == null ? 'yeni' : para(g.eski)} → ${para(g.yeni)}${g.fis ? ' · yeni belge' : ''}${g.aciklama ? ` · açıklama: “${g.aciklama}”` : ''}`;
        } else if (g.islem === 'olustur' || g.islem === 'plan_gonder') {
          const amir = g.alanlar?.amir ? `Amir: ${g.alanlar.amir[0]} → ${g.alanlar.amir[1]}` : g.amir_adi ? `Amir: ${g.amir_adi}` : '';
          const atlanan = (g.amir_atlanan || []).map((a) => `${a.ad} izinli (${a.izin})`).join(', ');
          detay = [amir, g.amir_kural ? `kural: ${g.amir_kural}` : '', atlanan ? `${atlanan} → bir üst kişi` : ''].filter(Boolean).join(' · ') || null;
        } else if (g.islem === 'rez_ilerlet') {
          detay = `${REZ_ASAMA_ADI[g.asama] || g.asama} → ${REZ_ASAMA_ADI[g.hedef] || g.hedef} · rezervasyon ${para(g.rez_toplam)}`;
        } else if (g.islem === 'rez_onay' || g.islem === 'rez_geri') {
          const isaret = g.isaretler ? ` · işaretli (${Object.entries(g.isaretler).map(([t, n]) => `${n} ${ISARET_TANIM[t]?.ad || t}`).join(', ')})` : '';
          detay = `${REZ_ASAMA_ADI[g.asama] || g.asama} → ${REZ_ASAMA_ADI[g.hedef] || g.hedef}${g.limit != null && g.asama === 'finans_onay' && g.islem === 'rez_onay' ? ` · limit ${para(g.limit)}` : ''}${g.vekil ? ` — TEST VEKİLİ, ${g.yerine} yerine` : ''}${isaret}`;
        } else if (g.islem === 'gider_sil') {
          detay = `${GIDER_ADI[g.gider_turu] || g.gider_turu}: ${para(g.eski)}`;
        } else if (g.islem === 'onay' || g.islem === 'geri_gonder') {
          const isaret = g.isaretler ? ` · işaretli (${Object.entries(g.isaretler).map(([t, n]) => `${n} ${ISARET_TANIM[t]?.ad || t}`).join(', ')})` : '';
          detay = `${ASAMA_ADI[g.asama] || g.asama}${g.vekil ? ` — TEST VEKİLİ, ${g.yerine} yerine` : ''}${isaret}`;
        } else if (g.islem === 'duzenle') {
          detay = Object.entries(g.alanlar || {})
            .map(([a, [eski, yeni]]) => `${ALAN_ADI[a] || a}: ${eski ?? '—'} → ${yeni ?? '—'}`).join(' · ');
        }
        const renk = {
          sil: 'red', gider_sil: 'red', geri_gonder: 'red', rez_geri: 'red', olustur: 'green', onay: 'green', rez_onay: 'green',
          gonder: 'purple', plan_gonder: 'purple',
        }[g.islem] || 'blue';
        return {
          color: renk,
          children: (
            <div>
              <Typography.Text strong>{ISLEM_ADI[g.islem] || g.islem}</Typography.Text>
              <Typography.Text type="secondary"> · {dayjs(g.zaman).format('DD.MM.YYYY HH:mm')} · {g.kisi_adi}</Typography.Text>
              {detay && <div>{detay}</div>}
              {g.neden && <div><Typography.Text italic>Neden: {g.neden}</Typography.Text></div>}
              {g.notu && <div><Typography.Text italic>Not: {g.notu}</Typography.Text></div>}
            </div>
          ),
        };
      })}
    />
  );
}

/** Fiş küçük resmi: oturumla indirilir; tıklanınca büyür. PDF/HEIC için düğme. giderId: gider belgesi. */
export function FisKucuk({ seyahatId, gunTarihi, ogun, giderId, fis }) {
  const api = useHarcirahApi('/harcirah-benim');
  const [url, setUrl] = useState(null);
  const resim = fis && ['image/jpeg', 'image/png', 'image/webp'].includes(fis.tur);
  useEffect(() => {
    if (!resim) return undefined;
    let iptal = false;
    let u = null;
    belgeIndir(api, seyahatId, gunTarihi, ogun, giderId).then((b) => {
      if (iptal) return;
      u = URL.createObjectURL(b);
      setUrl(u);
    }).catch(() => {});
    return () => { iptal = true; if (u) URL.revokeObjectURL(u); };
  }, [api, seyahatId, gunTarihi, ogun, giderId, resim]);
  if (!fis) return null;
  if (!resim) return <FisDugmesi seyahatId={seyahatId} gunTarihi={gunTarihi} ogun={ogun} giderId={giderId} fis={fis} etiket={fis.tur === 'application/pdf' ? 'PDF' : 'Dosya'} />;
  return url
    ? <Image src={url} width={44} height={56} style={{ objectFit: 'cover', borderRadius: 4 }} alt={giderId ? 'Gider Belgesi' : `${OGUN_ADI[ogun]} Fişi`} />
    : <span style={{ display: 'inline-block', width: 44, height: 56, borderRadius: 4, background: 'rgba(128,128,128,0.15)' }} />;
}

/**
 * Harcırah ekranlarının ortak stilleri. DarkMode.css koyu temada bütün .ant-tag / .ant-alert öğelerini !important ile
 * griye çeviriyor ve durum renklerini (error/warning/success) tanımlamıyor; işaretlerin kırmızısı kayboluyordu.
 * Yalnız harcırah sayfası (.hr-sayfa) ve pencereleri (rootClassName/className "hr-modal") için daha belirgin
 * seçicilerle geri getirilir; genel dosyaya dokunulmaz.
 */
export function HarcirahStilleri() {
  const k = "body[data-theme='dark'] :is(.hr-sayfa, .hr-modal)";
  return (
    <style>{`
      ${k} .ant-tag-error { background-color: rgba(255, 77, 79, 0.2) !important; border-color: #ff4d4f !important; color: #ff7875 !important; }
      ${k} .ant-tag-warning { background-color: rgba(250, 173, 20, 0.2) !important; border-color: #faad14 !important; color: #ffc53d !important; }
      ${k} .ant-tag-success { background-color: rgba(82, 196, 26, 0.2) !important; border-color: #52c41a !important; color: #73d13d !important; }
      ${k} .ant-tag-processing { background-color: rgba(24, 144, 255, 0.2) !important; border-color: #1890ff !important; color: #69b1ff !important; }
      ${k} .ant-alert-error { background-color: rgba(255, 77, 79, 0.12) !important; border-color: #ff4d4f !important; }
      ${k} .ant-alert-warning { background-color: rgba(250, 173, 20, 0.12) !important; border-color: #d89614 !important; }
      ${k} .ant-alert-success { background-color: rgba(82, 196, 26, 0.1) !important; border-color: #49aa19 !important; }
      ${k} .ant-alert-info { background-color: rgba(24, 144, 255, 0.1) !important; border-color: #177ddc !important; }
      ${k} .ant-btn-link { border-color: transparent !important; color: var(--color-primary, #1677ff) !important; }
      ${k} .ant-typography.ant-typography-danger { color: #ff7875 !important; }
      ${k} .ant-btn-dangerous:not(.ant-btn-primary) { border-color: #ff4d4f !important; color: #ff7875 !important; }
      ${k} .ant-btn-dangerous.ant-btn-primary { background-color: #ff4d4f !important; border-color: #ff4d4f !important; }
      /* DarkMode.css her primary düğmeyi maviye boyuyor; pasif düğme (ör. Onaya Gönder) etkin gibi görünmesin */
      ${k} .ant-btn:disabled { background-color: rgba(255, 255, 255, 0.08) !important; border-color: rgba(255, 255, 255, 0.15) !important;
                               color: rgba(255, 255, 255, 0.3) !important; }
      /* nfmodules.css çift satıra ve üzerine gelinen satıra koyu temayı bilmeyen açık zemin veriyor (yazı kayboluyordu) */
      ${k} .ant-table-tbody > tr:nth-child(even) > td { background: rgba(255, 255, 255, 0.025) !important; }
      ${k} .ant-table-tbody > tr:nth-child(odd) > td { background: transparent !important; }
      ${k} .ant-table-tbody > tr:hover > td { background: rgba(22, 119, 255, 0.12) !important; }
      ${k} .ant-table-tbody > tr.hr-isaretli-satir > td, ${k} .ant-table-tbody > tr.ok-isaretli-satir > td,
      ${k} .ant-table-tbody > tr > td.hr-isaretli-hucre { background: rgba(255, 77, 79, 0.16) !important; }
    `}</style>
  );
}
